import React, { useMemo } from 'react';
import { ProductCardProps } from '../types/types';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation, Pagination } from 'swiper/modules';
import Icon from '../../common/components/Icon';
import Chip from '../../components/chip/Chip';
import getDomainFromUrl from '../../utils/domainFromUrl';
import { formatPrice } from '../../common/Formatter';

function ProductImage({ image, permalink }: { image: { url: string }; permalink: string }) {
  return (
    <div className="life-product-card__image-wrapper">
      <a href={permalink}>
        <img src={image.url} alt="product" className="life-product-card__image" />
      </a>
    </div>
  );
}

function ProductCardImages({
  images,
  permalink,
  useSingleImage,
  disablePagination,
}: {
  images: any;
  permalink: string;
  useSingleImage: boolean;
  disablePagination: boolean;
}) {
  if (!Array.isArray(images) || images.length === 0) {
    return <></>;
  }

  if (useSingleImage || images.length === 1) {
    return <ProductImage image={images[0]} permalink={permalink} />;
  }

  return (
    <Swiper
      spaceBetween={6}
      slidesPerView={1}
      pagination={{
        type: 'bullets',
        enabled: !disablePagination,
        clickable: true,
        bulletClass: 'swiper-pagination-bullet',
        bulletActiveClass: 'swiper-pagination-bullet-active',
        dynamicBullets: true,
        dynamicMainBullets: 6,
      }}
      height={100}
      navigation={true}
      modules={[Navigation, Pagination]}
    >
      {images.map((image, index) => (
        <SwiperSlide key={index}>
          <ProductImage image={image} permalink={permalink} />
        </SwiperSlide>
      ))}
    </Swiper>
  );
}

const ProductCard: React.FC<ProductCardProps> = ({
  title,
  priceAmountNum: price_amount,
  priceCurrency: price_currency,
  url,
  permalink,
  urlDomain: url_domain,
  images,
  brand,
  vendor,
  labels,
  isLinkVisibleOnList: is_link_visible_on_list,
  hideBrand = false,
  disablePagination = false,
  useSingleImage = false,
}) => {
  const formattedPrice = useMemo(
    () => formatPrice(Number(price_amount), price_currency),
    [price_amount, price_currency]
  );

  return (
    <div className="life-product-card">
      <div className="life-product-card__chips">
        {labels &&
          labels.map((chip, index) => (
            <Chip name={chip.name} color={'amber'} type={'filled'} iconName={'circle-check'} key={index} />
          ))}
      </div>

      <ProductCardImages
        images={images}
        permalink={permalink}
        useSingleImage={useSingleImage}
        disablePagination={disablePagination}
      />

      <div className="life-product-card__description">
        <div className="life-product-card__title-wrapper">
          <div className="life-product-card__brand-wrapper">
            {vendor && (
              <>
                <a href={vendor?.url} className="life-product-card__brand basic-14">
                  {vendor?.name}
                </a>
                <span className="life-product-card__brand basic-14" style={{ marginLeft: '4px', marginRight: '4px' }}>
                  |
                </span>
              </>
            )}
            {brand && !hideBrand && (
              <a href={brand.url} className="life-product-card__brand basic-14">
                {brand.name}
              </a>
            )}
          </div>
          <a href={permalink} className="life-heading-wrapper-link">
            <h5>{title}</h5>
          </a>
        </div>

        <div className="life-product-card__price-row">
          <h6>{formattedPrice}</h6>
          {is_link_visible_on_list && (
            <div className="life-product-card__details-wrapper">
              <a href={url} target="_blank" rel="noreferrer" className="life-product-card__details-link basic-14">
                {url_domain ? url_domain : getDomainFromUrl(url)}
              </a>
              <Icon name="external-link" size="small" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
